import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useUser } from '@fs/zion-user'
import type { Playlist } from '../../api/rootstech/playlist/playlist.types'
import { getPlaylists as getPlaylistsApi } from '../../api'
import { useFlagsContext } from '../../providers/RootstechFlagsProvider'
import { FLAGS } from '../../config/flags'

type UsePlaylists = Omit<UseQueryResult, 'data'> & { playlists: Playlist[] }

const fetchAllPlaylists = (offset: string | null = null, acc: Playlist[] = []): Promise<Playlist[]> =>
  getPlaylistsApi(offset).then(({ data }) => {
    acc.push(...data.results)
    return data.nextPageToken ? fetchAllPlaylists(data.nextPageToken, acc) : acc
  })

export const usePlaylists = (): UsePlaylists => {
  const { signedIn } = useUser()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistState } = useFlagsContext()

  const { data: playlists = [], ...rest } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => fetchAllPlaylists().then((results) => ({ data: { results } })),
    staleTime: Infinity,
    enabled: newPlaylistState.isOn && signedIn,
    select: ({ data }) =>
      data.results.sort((a: Playlist, b: Playlist) => {
        if (a.default !== b.default) return a.default ? -1 : 1
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      }) as Playlist[],
  })
  return { playlists, ...rest }
}
